import { type TableColumnType, type TableColumnsType } from 'antd'

import {
  type ColumnLayoutDefinition,
  type ResolvedColumn
} from '../../-models/column-layout'

/** 表示項目設定で OFF にできない列（操作の入口: 一覧からのステータス変更・詳細への遷移。確定版 §2.2） */
export const UNHIDEABLE_COLUMN_KEYS: ReadonlySet<string> = new Set([
  'status',
  'orderNo'
])

/**
 * 常に固定し、動かせず、固定からも外せない列（確定版の「固定必須列」）。
 * #3690 Q-7 が 7-B（外せる）になったら空にする
 */
export const LOCKED_COLUMN_KEYS: ReadonlySet<string> = new Set([
  'status',
  'orderNo'
])

/**
 * 固定列の合計幅に残す余白（px）。表示幅からこの分を引いた幅を固定列の上限にする。
 * 行選択の列（固定）と、固定しない列を最低 1 列ぶん見せて、境界の右へ落とせるようにするため
 */
export const FIXED_WIDTH_MARGIN = 200

/** 幅を指定しない余白列の key。列幅の合計が表示幅より狭いときの余りを受ける */
export const FILLER_COLUMN_KEY = '__filler'

/** 列定義（既定の幅・固定）から、レイアウトのマージに要る情報を取り出す */
export const toColumnLayoutDefinitions = <T>(
  columns: TableColumnsType<T>
): ColumnLayoutDefinition[] =>
  columns.map((column) => ({
    key: String(column.key),
    defaultWidth: typeof column.width === 'number' ? column.width : 100,
    isFixedByDefault: Boolean(column.fixed),
    canHide: !UNHIDEABLE_COLUMN_KEYS.has(String(column.key)),
    isLocked: LOCKED_COLUMN_KEYS.has(String(column.key))
  }))

export type HeaderCellResizeProps = {
  width: number
  onResizeWidth: (width: number) => void
  onResizeEnd: (width: number) => void
}

/**
 * レイアウトを列定義に当て、Table に渡す列を作る。
 * - 並び・幅・固定はレイアウトに従い、非表示の列は外す
 * - 末尾に幅なしの余白列を置く。rc-table は列幅の合計が max(scroll.x, 表示幅) に満たないと、
 *   幅を指定していない列に余りを回し、それも無ければ全列を同じ比率で引き伸ばす
 *   （rc-table `hooks/useColumns/useWidthColumns`）。余白列があれば指定した幅どおりに描かれる
 */
export const applyColumnLayout = <T>(
  columns: TableColumnsType<T>,
  layout: ResolvedColumn[],
  resizePropsOf: (key: string, width: number) => HeaderCellResizeProps,
  options: { isLayoutEditing?: boolean } = {}
): TableColumnsType<T> => {
  const { isLayoutEditing = false } = options
  const columnByKey = new Map(
    columns.map((column) => [String(column.key), column] as const)
  )
  const laidOut = layout
    .filter(({ isHidden }) => !isHidden)
    .flatMap(({ key, width, isFixed, isLocked }) => {
      const column = columnByKey.get(key)
      if (!column) return []
      const next: TableColumnType<T> = {
        ...column,
        width,
        fixed: isFixed ? 'left' : undefined,
        // レイアウト編集モードの間は、誤操作を防ぐため見出しのクリックでのソートを止める（確定版 §2.3）
        ...(isLayoutEditing
          ? { sorter: false, sortOrder: null, showSorterTooltip: false }
          : {}),
        onHeaderCell: (data) => ({
          ...column.onHeaderCell?.(data),
          ...resizePropsOf(key, width),
          // 見出しのドラッグで、どの列のセルかと、つまみを出すかを見分ける（DraggableHeaderCell）
          ...{
            'data-column-key': key,
            'data-draggable': String(isLayoutEditing && !isLocked)
          }
        })
      }
      return [next]
    })

  return [...laidOut, { key: FILLER_COLUMN_KEY, title: '', render: () => null }]
}

/** Table の scroll.x。表示している列の幅の合計 */
export const sumVisibleWidth = (layout: ResolvedColumn[]): number =>
  layout
    .filter(({ isHidden }) => !isHidden)
    .reduce((total, { width }) => total + width, 0)
